import { Box, Stack, Typography } from "@mui/material";
import {
  TEXT_PRIMARY_DARK,
  TEXT_SECONDARY,
  VIOLET,
} from "@/config/mui/colorPalette";
import useMoxieMediaQuery from "@/hooks/misc/useMoxieMediaQuery";
import {
  generatePackageItemOnlineStoreName,
  generatePackageItemOnlineStoreAmount,
} from "@/utils/packages";
import { MedspaPackage } from "@/views/online-store/onlineStoreView";

type PackageItem = MedspaPackage["packageItems"][number];

type PackageItemsListProps = {
  packageItems: MedspaPackage["packageItems"];
  showAll?: boolean;
  maxVisibleItems?: number;
  variant?: "default" | "modal";
};

type MobilePackageListProps = {
  visibleItems: PackageItem[];
  shouldShowRemainingItems: boolean;
  remainingItemsCount: number;
};

function MobilePackageList({
  visibleItems,
  shouldShowRemainingItems,
  remainingItemsCount,
}: MobilePackageListProps) {
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
      <Typography
        variant="labelTiny"
        sx={{ color: TEXT_SECONDARY, display: "inline-block" }}
      >
        {visibleItems.map((item, index) => {
          const isLastItem = index === visibleItems.length - 1;
          const itemName = generatePackageItemOnlineStoreName(item);
          const separator = isLastItem && !shouldShowRemainingItems ? "" : ", ";

          return `${itemName}${separator}`;
        })}
        {shouldShowRemainingItems && `+${remainingItemsCount} more`}
      </Typography>
    </Box>
  );
}

export default function PackageItemsList({
  packageItems,
  showAll = false,
  maxVisibleItems = 2,
  variant = "default",
}: PackageItemsListProps) {
  const isMobile = useMoxieMediaQuery("ipad-ver", "down");
  const visibleItems = showAll
    ? packageItems
    : packageItems.slice(0, maxVisibleItems);
  const remainingItemsCount = packageItems.length - maxVisibleItems;
  const shouldShowRemainingItems = !showAll && remainingItemsCount > 0;

  if (isMobile && variant === "default") {
    return (
      <MobilePackageList
        visibleItems={visibleItems}
        shouldShowRemainingItems={shouldShowRemainingItems}
        remainingItemsCount={remainingItemsCount}
      />
    );
  }

  const textVariant = variant === "modal" ? "labelDefault" : "labelTiny";

  return (
    <Stack sx={{ gap: variant === "modal" ? 0.5 : 0 }}>
      {visibleItems.map((item) => (
        <Stack
          key={item.id}
          sx={{
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flex: 1,
              minWidth: 0,
            }}
          >
            <Box
              sx={{
                width: 6,
                height: 6,
                backgroundColor: VIOLET[80],
                borderRadius: "50%",
                flexShrink: 0,
              }}
            />
            <Typography
              variant={textVariant}
              sx={{
                color: TEXT_PRIMARY_DARK,
                ...(!showAll && {
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: "100%",
                  display: "block",
                  flex: 1,
                }),
              }}
            >
              {generatePackageItemOnlineStoreName(item)}
            </Typography>
          </Box>
          {showAll && (
            <Typography variant={textVariant} sx={{ color: TEXT_SECONDARY }}>
              {generatePackageItemOnlineStoreAmount(item)}
            </Typography>
          )}
        </Stack>
      ))}
      {shouldShowRemainingItems && (
        <Typography variant="labelTiny" sx={{ color: TEXT_SECONDARY }}>
          +{remainingItemsCount} more
        </Typography>
      )}
    </Stack>
  );
}
